import { ImageWithLoader } from '@overlayqa/shared/ui';
import { useState, useRef, useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { MoreVerticalIcon, Bug01Icon } from '@hugeicons/core-free-icons';
import type { ProjectMember, ProjectWithPreview } from '../../lib/api/projects';
import { getProjectIconAndColor } from '@overlayqa/shared';
import { timeAgo } from '../../lib/utils';
import { resolveAssetUrl } from '@/lib/api/client';
import { isProjectOwner, SETTINGS_VIEW_ONLY } from './projectsPageColumns';
import { canOpenProjectSettings } from '../../components/projects/settings/projectSettingsTabs';
import { canWriteProject } from '../../lib/projectPermissions';
import { Tooltip } from '../../components/shared/Tooltip';
import { Avatar } from '../../components/ui/Avatar';

const MAX_AVATARS = 3;
/** Names listed in the people tooltip before it counts the rest. */
const MAX_TOOLTIP_NAMES = 5;

interface ProjectCardProps {
  project: ProjectWithPreview;
  /**
   * Opens the Project Settings modal (ProjectSettingsModal). Every handler is
   * optional: with none passed the card renders no actions menu at all, which
   * is how the dashboard's My Projects section uses it (RecentProjectsSection).
   */
  onOpenSettings?: (id: string) => void;
  onArchive?: (id: string) => void;
  onDelete?: (id: string) => void;
  onClientReview?: (projectId: string) => void;
  onLiveReview?: (projectId: string) => void;
  /**
   * "Capture a different page": the URL prompt, for a page or site other
   * than the one Capture Issues resolves from the project (useCaptureIssues).
   */
  onLiveReviewOther?: (projectId: string) => void;
  /**
   * True in the "Archived" view (ProjectsPage's showArchived toggle). Swaps
   * the action menu to Unarchive + Delete only — Settings/Archive/Team
   * Review/Client Review don't make sense for a project that isn't
   * reachable from the normal list. `onUnarchive` is required alongside it.
   */
  archived?: boolean;
  onUnarchive?: (id: string) => void;
  /**
   * Replaces the preview image inside the thumbnail frame. The dashboard uses
   * it for the pending-extension-open states ("Opening..." / "Install the
   * extension to open this site") on the site the user chose in onboarding.
   */
  thumbnail?: ReactNode;
}

export function ProjectCard({ project, onOpenSettings, onArchive, onDelete, onClientReview, onLiveReview, onLiveReviewOther, archived = false, onUnarchive, thumbnail }: ProjectCardProps) {
  const { icon: iconDef, color: colorDef } = getProjectIconAndColor(project);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // Archive/Delete are owner-only server-side (403 for everyone else) —
  // disable them with the reason instead of letting the click fail.
  const canManage = isProjectOwner(project);
  const canOpenSettings = canOpenProjectSettings(project.role);
  const canCapture = canWriteProject(project.role);
  const hasActions = Boolean(onOpenSettings || onArchive || onDelete || onUnarchive || (canCapture && (onClientReview || onLiveReview || onLiveReviewOther)));

  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const title = (
    <h3 className="text-sm font-medium text-gray-900 truncate group-hover:text-primary-600 transition-colors">
      {project.name}
    </h3>
  );

  return (
    <article className="card-sm card-interactive relative group">
      <Link
        to={`/projects/${project.id}/issues`}
        className="block"
      >
        {/* Thumbnail */}
        <div className="aspect-[3/2] rounded-lg bg-gray-100 overflow-hidden mb-3 relative border border-gray-200">
          {thumbnail ? thumbnail : project.previewUrl ? (
            <ImageWithLoader fill
              src={resolveAssetUrl(project.previewUrl)}
              alt={`${project.name} preview`}
              className="w-full h-full object-cover object-top"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg className="w-10 h-10" style={{ color: colorDef.hex }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d={iconDef.path} />
              </svg>
            </div>
          )}
        </div>


        {/* Info row: icon + title left, role badge + dots right. h-5 pins the row
            to the title's 20px line: a 21px badge used to make badged cards 1px
            taller than the rest (measured 2026-09-18). */}
        <div className="flex items-center gap-2 min-w-0 h-5">
          <svg className="w-4 h-4 shrink-0 text-gray-200 group-hover:text-[--project-color] transition-colors" style={{ '--project-color': colorDef.hex } as React.CSSProperties} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={iconDef.path} />
          </svg>
          {/* The domain lives in a tooltip on the title (and in the link's
              name for screen readers), not as card text. */}
          {project.primaryDomain ? (
            <>
              <Tooltip label={project.primaryDomain} placement="top" multiline>{title}</Tooltip>
              <span className="sr-only">{project.primaryDomain}</span>
            </>
          ) : title}
          {/* Role badges sit at the right, beside the actions button (Emily,
              2026-09-18), so the title keeps the left of the row to itself. */}
          <div className="flex-1" />
          {project.role === 'collaborator' && (
            <span className="badge-xs bg-primary-50 text-primary-700 border border-primary-200 shrink-0">
              Shared
            </span>
          )}
          {project.role === 'viewer' && (
            <span className="badge-xs bg-gray-50 text-gray-500 border border-gray-200 shrink-0">
              View only
            </span>
          )}
          {project.role === 'team' && (
            <span className="badge-xs bg-green-50 text-green-700 border border-green-200 shrink-0">
              Team
            </span>
          )}
          {archived && (
            <span className="badge-xs bg-gray-100 text-gray-500 border border-gray-200 shrink-0">
              Archived
            </span>
          )}
          {/* Action menu — inline with info row; absent when no handler is wired */}
          {hasActions && <div ref={menuRef} className="relative shrink-0">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenuOpen(!menuOpen);
              }}
              className="flex items-center justify-center text-gray-300 hover:text-gray-500 transition-colors"
              aria-label="Project actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <HugeiconsIcon icon={MoreVerticalIcon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4" aria-hidden="true" />
            </button>

            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 bottom-full mb-1 min-w-[8rem] bg-white rounded-lg border border-gray-200 shadow-lg py-1 z-10"
              >
                {archived ? (
                  <button
                    role="menuitem"
                    className={`w-full text-left px-3 py-2 text-sm whitespace-nowrap ${canManage ? 'text-primary-600 hover:bg-primary-50 font-medium' : 'text-gray-300 cursor-not-allowed'}`}
                    disabled={!canManage}
                    title={canManage ? undefined : 'Only the project owner can unarchive this project'}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onUnarchive?.(project.id);
                      setMenuOpen(false);
                    }}
                  >
                    <span className="text-cap-trim block">Unarchive</span>
                  </button>
                ) : (
                  <>
                    <button
                      role="menuitem"
                      className={`w-full text-left px-3 py-2 text-sm whitespace-nowrap ${canOpenSettings ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-300 cursor-not-allowed'}`}
                      disabled={!canOpenSettings}
                      title={canOpenSettings ? undefined : SETTINGS_VIEW_ONLY}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onOpenSettings?.(project.id);
                        setMenuOpen(false);
                      }}
                    >
                      <span className="text-cap-trim block">Settings</span>
                    </button>
                    {canCapture && onLiveReview && (
                      <button
                        role="menuitem"
                        className="w-full text-left px-3 py-2 text-sm whitespace-nowrap text-primary-600 hover:bg-primary-50 font-medium"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onLiveReview(project.id);
                          setMenuOpen(false);
                        }}
                      >
                        <span className="text-cap-trim block">Capture issues</span>
                      </button>
                    )}
                    {canCapture && onLiveReviewOther && (
                      <button
                        role="menuitem"
                        className="w-full text-left px-3 py-2 text-sm whitespace-nowrap text-gray-700 hover:bg-gray-50"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onLiveReviewOther(project.id);
                          setMenuOpen(false);
                        }}
                      >
                        <span className="text-cap-trim block">Capture a different page</span>
                      </button>
                    )}
                    {canCapture && onClientReview && (
                      <button
                        role="menuitem"
                        className="w-full text-left px-3 py-2 text-sm whitespace-nowrap text-gray-700 hover:bg-gray-50"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onClientReview(project.id);
                          setMenuOpen(false);
                        }}
                      >
                        <span className="text-cap-trim block">Share client link</span>
                      </button>
                    )}
                    <button
                      role="menuitem"
                      className={`w-full text-left px-3 py-2 text-sm whitespace-nowrap ${canManage ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-300 cursor-not-allowed'}`}
                      disabled={!canManage}
                      title={canManage ? undefined : 'Only the project owner can archive this project'}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onArchive?.(project.id);
                        setMenuOpen(false);
                      }}
                    >
                      <span className="text-cap-trim block">Archive</span>
                    </button>
                  </>
                )}
                <button
                  role="menuitem"
                  className={`w-full text-left px-3 py-2 text-sm whitespace-nowrap ${canManage ? 'text-red-600 hover:bg-red-50' : 'text-gray-300 cursor-not-allowed'}`}
                  disabled={!canManage}
                  title={canManage ? undefined : 'Only the project owner can delete this project'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDelete?.(project.id);
                    setMenuOpen(false);
                  }}
                >
                  <span className="text-cap-trim block">Delete</span>
                </button>
              </div>
            )}
          </div>}
        </div>

        {/* Meta row: updated left; issue count + people with access grouped right */}
        <div className="flex items-center justify-between gap-3 mt-3 min-h-6">
          <span className="text-xs text-gray-400 truncate">Updated {timeAgo(project.updatedAt)}</span>
          <div className="flex items-center gap-3 shrink-0">
            {typeof project.ticketCount === 'number' && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <HugeiconsIcon icon={Bug01Icon} size={14} strokeWidth={2} color="currentColor" className="w-3.5 h-3.5" aria-hidden="true" />
                <span>{project.ticketCount}</span>
                <span className="sr-only"> issues</span>
              </span>
            )}
            <MemberStack members={project.members} />
          </div>
        </div>
      </Link>
    </article>
  );
}

/**
 * Up to MAX_AVATARS avatars, then "+N"; every name on hover.
 *
 * The overlap is 1px on purpose. Two-letter initials in Plus Jakarta Sans fill
 * ~16px of the 24px avatar, and the next avatar's 2px ring covers that much
 * again, so the usual 6px overlap cut "MC" down to "M(" by 4.2px (measured
 * 2026-09-18 in the states preview).
 */
function MemberStack({ members }: { members?: ProjectMember[] }) {
  if (!members?.length) return null;
  const names = members.map((m) => m.name).join(', ');
  const extra = members.length - MAX_AVATARS;
  const unnamed = members.length - MAX_TOOLTIP_NAMES;
  const tooltip = unnamed > 0
    ? `${members.slice(0, MAX_TOOLTIP_NAMES).map((m) => m.name).join(', ')} and ${unnamed} more`
    : names;
  return (
    // top-end: the stack sits at the card's right edge, so a centred bubble
    // would run off screen in the grid's last column.
    <Tooltip label={tooltip} placement="top-end" multiline>
      <div role="img" aria-label={`People with access: ${names}`} className="flex items-center -space-x-px shrink-0">
        {members.slice(0, MAX_AVATARS).map((m) => (
          <Avatar key={m.id} src={m.avatarUrl} name={m.name} id={m.id} size="sm" className="ring-2 ring-white" />
        ))}
        {extra > 0 && (
          <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-[10px] font-medium flex items-center justify-center shrink-0 ring-2 ring-white">
            +{extra}
          </span>
        )}
      </div>
    </Tooltip>
  );
}
